import styled, { css } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";
import { buttonLoadingCss } from "../../../../components/Button";

export const Container = styled.div`
  position: relative;
  min-height: 100dvh;

  /* The global reset gives every element the system font: the whole screen is Inter, like the plans screen */
  &,
  & * {
    font-family: Inter, sans-serif;
  }
  /* The same soft green glow as the plans screen, so the two read as one flow */
  background:
    radial-gradient(60% 360px at 50% 0%, rgba(87, 242, 139, 0.1) 0%, rgba(87, 242, 139, 0) 70%),
    var(--color-neutral-darkness);
`;

/* One narrow column on every screen size, the same as on phones: title, the order summary (collapsed),
   then the form */
export const Layout = styled.div<{ $flow?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  max-width: 520px;
  padding: 4.5rem 0 4rem;
  margin: 0 auto;

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    padding: 3.75rem 20px 3rem;
  }

  /* In the creation flow: the same narrow column, right under the steps' header (the bar holds the button) */
  ${props =>
    props.$flow &&
    css`
      padding: 1.5rem 0 0;

      @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
        padding: 20px 20px 0;
      }
    `}
`;

/* Your details, payment method and the button: blocks set apart by space and a thin line */
export const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-width: 0;
  margin-top: 0.75rem;

  > section + section,
  > section + div {
    padding-top: 2rem;
    border-top: 1px solid var(--color-neutral-dark);
  }

  .method-error {
    margin-top: 0.75rem;
    font-size: 0.8125rem;
    color: var(--color-feedback-danger);
  }

  h2 {
    margin-bottom: 1rem;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 800;
    color: var(--color-neutral-150);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

/* The screen title */
export const Header = styled.header`
  h1 {
    font-size: 1.75rem;
    font-weight: 800;
    line-height: 2.125rem;
    letter-spacing: -0.02em;
    color: var(--color-neutral-white);
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    h1 {
      font-size: 1.375rem;
      line-height: 1.75rem;
    }
  }
`;

/* One card per method, the chosen one opens up (accordion, as in most checkouts) */
export const MethodList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;

  > li {
    position: relative;
    background: rgba(38, 42, 52, 0.5);
    border: 1px solid var(--color-neutral-base);
    border-radius: 1rem;
    transition:
      border-color 0.15s,
      background-color 0.15s;

    &:not(.locked):hover {
      border-color: var(--color-neutral-light);
    }

    &.selected {
      background: rgba(38, 42, 52, 0.85);
      border-color: var(--color-brand-plus);
      box-shadow: inset 0 0 0 1px var(--color-brand-plus);
    }

    &.locked .method {
      cursor: not-allowed;

      .icon,
      .text b,
      .radio {
        opacity: 0.4;
      }
    }
  }

  .method {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    width: 100%;
    padding: 1rem 1.125rem;
    text-align: left;
  }

  .radio {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: 2px solid var(--color-neutral-light);
    border-radius: 100%;
  }

  .selected .radio {
    border: 5px solid var(--color-brand-plus);
  }

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: var(--color-neutral-white);
    background: rgba(255, 255, 255, 0.06);
    border-radius: 10px;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.1875rem;
    min-width: 0;

    b {
      font-size: 0.9375rem;
      font-weight: 700;
      color: var(--color-neutral-white);
    }

    > span {
      font-size: 0.75rem;
      color: var(--color-neutral-150);
    }
  }

  .brands {
    display: flex;
    flex-shrink: 0;
    gap: 0.25rem;
  }

  .tag {
    flex-shrink: 0;
    padding: 0.125rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 800;
    color: var(--color-brand-plus);
    text-transform: uppercase;
    background: rgba(87, 242, 139, 0.15);
    border-radius: 100px;
  }

  /* Inside the locked Pix and boleto (monthly plan), on the right: switch to the yearly plan */
  > li.locked .method {
    padding-right: 10rem;
  }

  .switch {
    position: absolute;
    top: 50%;
    right: 1.125rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 800;
    color: var(--color-brand-plus);
    border: 1px solid rgba(87, 242, 139, 0.45);
    border-radius: 100px;
    transform: translateY(-50%);
    transition: background-color 0.15s;

    &:hover {
      background: rgba(87, 242, 139, 0.1);
    }
  }

  .extra {
    padding: 0 1.125rem 1.125rem;
  }

  .info {
    padding: 0 1.125rem 1rem 4.75rem;
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }

  /* Card brands as small chips */
  .brand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    height: 20px;
    padding: 0 0.25rem;
    font-size: 0.5625rem;
    font-weight: 900;
    color: #1a1f71;
    letter-spacing: 0.02em;
    background: #fff;
    border-radius: 4px;

    &.visa {
      font-style: italic;
    }

    &.amex {
      color: #fff;
      background: #2e77bc;
    }

    &.elo {
      font-size: 0.6875rem;
      color: #fff;
      text-transform: lowercase;
      background: #000;
      box-shadow: inset 0 0 0 1px var(--color-neutral-light);
    }

    &.hipercard {
      color: #fff;
      background: #b3131b;
    }
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    .brands {
      display: none;
    }

    /* Phones: the switch goes under the text */
    > li.locked .method {
      padding-right: 1.125rem;
    }

    .switch {
      position: static;
      margin: -0.375rem 0 1rem 4.625rem;
      transform: none;
    }

    .info {
      padding-left: 1.125rem;
    }
  }
`;

/* Labelled fields in two columns (`.full` spans both): the card, the billing details */
const fieldGridCss = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem 0.75rem;

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: 0;

    &.full {
      grid-column: 1 / -1;
    }

    &.full-mobile {
      @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
        grid-column: 1 / -1;
      }
    }

    .label {
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--color-neutral-150);
    }

    small {
      font-size: 0.75rem;
      color: var(--color-feedback-danger);

      &.hint {
        color: var(--color-neutral-150);
      }
    }
  }

  .control {
    position: relative;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    padding: 0 0.875rem;
    background: var(--color-neutral-darkness);
    border: 1px solid var(--color-neutral-base);
    border-radius: 10px;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;

    &:focus-within {
      border-color: var(--color-brand-plus);
      box-shadow: 0 0 0 3px rgba(87, 242, 139, 0.15);
    }

    input,
    select {
      flex: 1;
      min-width: 0;
      height: 46px;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--color-neutral-white);
      background-color: transparent;
      outline: none;

      &::placeholder {
        font-weight: 500;
        color: var(--color-neutral-light);
      }
    }

    /* A small link inside the field, on the right (e.g. "Não sei meu CEP") */
    .inline-link {
      flex-shrink: 0;
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--color-neutral-150);
      white-space: nowrap;
      text-decoration: underline;
      text-underline-offset: 2px;

      &:hover {
        color: var(--color-neutral-white);
      }
    }
  }

  /* Stripe's card fields (iframes) take the input's place */
  .stripe-field {
    flex: 1;
    min-width: 0;
    height: 46px;
  }

  .flag {
    font-size: 1.125rem;
    line-height: 1;
  }

  /* Native select, with its own arrow */
  select {
    padding-right: 1.25rem;
    appearance: none;
    cursor: pointer;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%235e6272' stroke-width='1.6' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")
      no-repeat right center;

    option {
      color: var(--color-neutral-white);
      background: var(--color-neutral-darkness);
    }
  }

  .checkbox {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--color-neutral-150);
    cursor: pointer;

    &.full {
      grid-column: 1 / -1;
    }

    input {
      width: 16px;
      height: 16px;
      accent-color: var(--color-brand-plus);
    }
  }

  .invalid .control {
    border-color: var(--color-feedback-danger);
  }
`;

export const FieldGrid = styled.div`
  ${fieldGridCss}
`;

export const CardForm = styled.div`
  ${fieldGridCss}
  padding: 0.25rem 1.125rem 1.125rem;
`;

/* The single call to action, right under the form */
export const Submit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;

  .cta {
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 1.0625rem 1.5rem;
    font-size: 1rem;
    font-weight: 800;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 100px;
    box-shadow: 0 10px 30px rgba(75, 16, 255, 0.45);
    transition: filter 0.15s;

    &:hover:not(:disabled) {
      filter: brightness(1.12);
    }

    &:disabled {
      cursor: progress;
      opacity: 0.7;
    }

    /* Processing: Keepo's loading (the Button's), the label hidden */
    &[aria-busy="true"] {
      ${buttonLoadingCss}
      box-shadow: none;

      &:disabled {
        opacity: 1;
      }
    }
  }

  .error {
    font-size: 0.875rem;
    color: var(--color-feedback-danger);
    text-align: center;
  }

  .secure {
    display: flex;
    gap: 0.375rem;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    color: var(--color-neutral-150);
  }

  .legal {
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: var(--color-neutral-light);
    text-align: center;
  }
`;

/* The order: one line with the plan and total, the details on demand */
export const Summary = styled.aside<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  border-radius: 1.25rem;

  .toggle {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-white);

    .label {
      display: flex;
      gap: 0.375rem;
      align-items: center;
      color: var(--color-brand-plus);

      svg {
        transform: rotate(${props => (props.$open ? "-90deg" : "90deg")});
        transition: transform 0.2s;
      }
    }

    b {
      font-size: 1rem;
      font-weight: 800;

      small {
        font-size: 0.8125rem;
        font-weight: 700;
        color: var(--color-neutral-150);
      }
    }
  }

  .details {
    display: ${props => (props.$open ? "block" : "none")};
    margin-top: 1rem;
  }

  .keepo {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: space-between;

    span:last-child {
      overflow: hidden;
      font-size: 0.8125rem;
      font-weight: 700;
      color: var(--color-neutral-white);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  dl {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding: 1rem 0;
    margin-top: 1rem;
    font-size: 0.8125rem;
    border-top: 1px solid var(--color-neutral-base);
    border-bottom: 1px solid var(--color-neutral-base);

    div {
      display: flex;
      gap: 1rem;
      justify-content: space-between;
    }

    dt {
      color: var(--color-neutral-150);
    }

    dd {
      font-weight: 700;
      color: var(--color-neutral-white);
      white-space: nowrap;
    }

    .saving dd {
      color: var(--color-brand-plus);
    }
  }

  .total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-top: 1rem;

    span {
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--color-neutral-white);
    }

    b {
      font-size: 1.5rem;
      font-weight: 800;
      color: var(--color-neutral-white);
    }
  }

  .renewal {
    margin-top: 0.75rem;
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
  }
`;

/* Yearly / monthly, switchable right in the summary */
export const PlanSwitch = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem;
  padding: 0.25rem;
  margin-top: 1rem;
  background: var(--color-neutral-darkness);
  border-radius: 100px;

  button {
    display: inline-flex;
    gap: 0.375rem;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-150);
    border-radius: 100px;
    transition:
      color 0.15s,
      background-color 0.15s;

    &[aria-checked="true"] {
      color: var(--color-neutral-darkness);
      background: var(--color-brand-plus);
    }

    small {
      font-size: 0.625rem;
      font-weight: 800;
    }
  }
`;

/* "Tem um cupom?": a link that opens into a field */
export const Coupon = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-top: 0.875rem;

  .link {
    align-self: flex-start;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-brand-plus);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  form {
    display: flex;
    gap: 0.5rem;
  }

  input {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 0.75rem;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    letter-spacing: 0.04em;
    background: var(--color-neutral-darkness);
    border: 1px solid var(--color-neutral-base);
    border-radius: 10px;
    outline: none;

    &:focus {
      border-color: var(--color-brand-plus);
    }

    &::placeholder {
      font-weight: 500;
      color: var(--color-neutral-light);
      letter-spacing: normal;
    }
  }

  form button {
    display: flex;
    align-items: center;
    justify-content: center;
    /* Same width with the label or the loading symbol */
    min-width: 5.5rem;
    padding: 0 1rem;
    font-size: 0.8125rem;
    font-weight: 800;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 10px;

    &:disabled:not([aria-busy="true"]) {
      opacity: 0.5;
    }
  }

  input:disabled {
    opacity: 0.6;
  }

  small {
    font-size: 0.75rem;
    color: var(--color-feedback-danger);
  }
`;

/* The countries under the country field, filtered as you type */
export const CountryList = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  left: 0;
  z-index: 20;
  max-height: 260px;
  padding: 0.375rem;
  overflow-y: auto;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);

  li {
    display: flex;
    gap: 0.625rem;
    align-items: center;
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-neutral-white);
    cursor: pointer;
    border-radius: 8px;

    &.active {
      background: rgba(255, 255, 255, 0.06);
    }

    &[aria-selected="true"] {
      color: var(--color-brand-plus);
    }

    /* Between the countries on top and the alphabetical rest */
    &.divided {
      position: relative;
      margin-top: 0.625rem;

      &::before {
        position: absolute;
        top: -0.375rem;
        right: 0.5rem;
        left: 0.5rem;
        height: 1px;
        content: "";
        background: var(--color-neutral-base);
      }
    }

    &.empty {
      font-weight: 500;
      color: var(--color-neutral-150);
      cursor: default;
    }
  }
`;

/* The wallets' availability check: Stripe's element mounted out of sight (it has to be in the page) */
export const WalletProbe = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  pointer-events: none;
  opacity: 0;
  clip-path: inset(50%);
`;

/** Disables every field and button inside it (a fieldset's own), without changing the layout. */
export const Lock = styled.fieldset`
  display: contents;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;
